import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";

import type {
  SenderCandidate,
  UnresolvedMessage,
} from "../entities/counterparties";
import { resolutionMethodLabel } from "../entities/counterparties";
import {
  formatDate,
  requestErrorText,
  useSubmissionKey,
} from "./counterpartiesSupport";
import type { ShellServices } from "./services";
import { counterpartyKeys } from "./useCounterpartyNames";
import { useDebouncedValue } from "./useDebouncedValue";
import { Select } from "./Select";
import { Notice } from "./Notice";

type CounterpartyService = NonNullable<ShellServices["counterparties"]>;

const INBOX_LIMIT = 50;

export type InboxState = "pending" | "unresolved" | "conflict";

const stateLabels: Record<InboxState, string> = {
  pending: "Требуют разбора",
  unresolved: "Отправитель не найден",
  conflict: "Конфликт",
};

function senderLabel(message: UnresolvedMessage): string {
  const from = message.from;
  if (from === null) return "Отправитель не указан";
  return from.name ? `${from.name} <${from.email}>` : from.email;
}

function StateBadge({ method }: { method: string | null }) {
  const conflict = method === "conflict";
  return (
    <span
      className={`status-badge ${conflict ? "status-badge--pending cp-badge--conflict" : "status-badge--expired"}`}
    >
      {conflict ? "Конфликт" : "Не определён"}
    </span>
  );
}

/**
 * Выбор контрагента для письма: кандидаты сервера и поиск по всему
 * справочнику. Ничего не выбрано заранее — даже когда кандидат один: решение
 * принимает человек.
 */
function TargetPicker({
  service,
  candidates,
  conflict,
  selected,
  onSelect,
}: {
  service: CounterpartyService;
  candidates: readonly SenderCandidate[];
  conflict: boolean;
  selected: string | null;
  onSelect: (id: string, name: string) => void;
}) {
  const name = useId();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search.trim());
  const found = useQuery({
    queryKey: [...counterpartyKeys.all, "picker", debounced],
    queryFn: () =>
      service.list({
        per_page: 10,
        ...(debounced === "" ? {} : { search: debounced }),
      }),
    enabled: debounced !== "",
  });
  const best = Math.min(...candidates.map((item) => item.match_priority));
  const tied = candidates.filter((item) => item.match_priority === best);
  const candidateIds = new Set(candidates.map((item) => item.counterparty_id));

  return (
    <div className="cp-picker-groups">
      {candidates.length === 0 ? (
        <p className="cp-empty">
          По этому отправителю система не нашла ни одного контрагента. Найдите
          нужного ниже или создайте нового на вкладке «Контрагенты».
        </p>
      ) : (
        <fieldset className="cp-fieldset">
          <legend>Кого нашла система</legend>
          <ul className="cp-radio-list">
            {candidates.map((candidate) => {
              const isTied =
                conflict && tied.length > 1 && tied.includes(candidate);
              return (
                <li key={candidate.counterparty_id}>
                  <label className="cp-radio">
                    <input
                      type="radio"
                      name={name}
                      checked={selected === candidate.counterparty_id}
                      onChange={() =>
                        onSelect(candidate.counterparty_id, candidate.name)
                      }
                    />
                    <span className="cp-radio__body">
                      <strong>{candidate.name}</strong>
                      <span className="muted">
                        {[
                          candidate.inn ? `ИНН ${candidate.inn}` : null,
                          candidate.kpp ? `КПП ${candidate.kpp}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "Реквизиты не указаны"}
                      </span>
                      <span className="muted">
                        {resolutionMethodLabel(candidate.match_method)}
                      </span>
                      <span className="cp-radio__flags">
                        {isTied ? (
                          <span className="status-badge status-badge--pending">
                            Равное основание
                          </span>
                        ) : null}
                        {candidate.is_current ? (
                          <span className="status-badge status-badge--active">
                            Сейчас назначен
                          </span>
                        ) : null}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}

      <fieldset className="cp-fieldset">
        <legend>Другой контрагент</legend>
        <div className="field cp-field">
          <label htmlFor={`${name}-search`}>
            <span>Поиск по названию или ИНН</span>
          </label>
          <input
            id={`${name}-search`}
            type="search"
            value={search}
            autoComplete="off"
            maxLength={255}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {debounced === "" ? null : found.isLoading ? (
          <p aria-busy="true">Ищем…</p>
        ) : found.isError ? (
          <Notice>
            {requestErrorText(found.error, "Не удалось выполнить поиск.")}
          </Notice>
        ) : (found.data?.data ?? []).length === 0 ? (
          <p className="muted">Ничего не найдено.</p>
        ) : (
          <ul className="cp-radio-list">
            {(found.data?.data ?? [])
              .filter((item) => !candidateIds.has(item.id))
              .map((item) => (
                <li key={item.id}>
                  <label className="cp-radio">
                    <input
                      type="radio"
                      name={name}
                      checked={selected === item.id}
                      onChange={() => onSelect(item.id, item.name)}
                    />
                    <span className="cp-radio__body">
                      <strong>{item.name}</strong>
                      <span className="muted">
                        {[
                          item.inn ? `ИНН ${item.inn}` : null,
                          item.kpp ? `КПП ${item.kpp}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || "Реквизиты не указаны"}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </fieldset>
    </div>
  );
}

function MessageResolution({
  service,
  message,
  canManage,
  onResolved,
}: {
  service: CounterpartyService;
  message: UnresolvedMessage;
  canManage: boolean;
  onResolved: (targetName: string | null, remembered: boolean) => void;
}) {
  const client = useQueryClient();
  const key = useSubmissionKey();
  const [target, setTarget] = useState<{ id: string; name: string } | null>(
    null,
  );
  const selected = target?.id ?? null;
  const [remember, setRemember] = useState(false);
  const conflict = message.sender_resolution_method === "conflict";

  const candidates = useQuery({
    queryKey: [...counterpartyKeys.all, "candidates", message.id],
    queryFn: () => service.candidates(message.id),
    enabled: canManage,
  });
  const list = candidates.data ?? [];
  const best =
    list.length === 0
      ? null
      : Math.min(...list.map((item) => item.match_priority));
  const tiedCount =
    best === null
      ? 0
      : list.filter((item) => item.match_priority === best).length;

  const resolve = useMutation({
    mutationFn: () =>
      service.resolve(
        message.id,
        {
          counterparty_id: selected ?? "",
          ...(remember ? { remember_rule: true } : {}),
        },
        key.current(),
      ),
    onSuccess: async () => {
      key.reset();
      onResolved(target?.name ?? null, remember);
      await client.invalidateQueries({ queryKey: counterpartyKeys.all });
    },
  });

  return (
    <div className="cp-resolution">
      <header>
        <h3>{message.subject ?? "Без темы"}</h3>
        <p className="muted">
          {senderLabel(message)} · получено {formatDate(message.received_at)}
        </p>
      </header>

      {conflict ? (
        <div className="cp-conflict" role="note">
          <strong>Автоматический выбор невозможен</strong>
          <p>
            {tiedCount > 1
              ? `Сразу ${tiedCount} контрагента подошли по основанию одинаковой силы. `
              : "Несколько контрагентов подошли по основанию одинаковой силы. "}
            Система не выбирает между ними сама: письмо остаётся без
            контрагента, пока вы не решите.
          </p>
        </div>
      ) : (
        <p className="cp-note">
          Отправитель не совпал ни с одним контрагентом по адресу или домену.
          Название, ИНН и КПП отправителя не учитываются: их пишет сам
          отправитель.
        </p>
      )}

      {!canManage ? (
        <p className="muted">
          Сопоставлять письма может сотрудник с правом «Управление
          контрагентами».
        </p>
      ) : (
        <>
          {candidates.isLoading ? (
            <p aria-busy="true">Загружаем кандидатов…</p>
          ) : null}
          {candidates.isError ? (
            <Notice>
              {requestErrorText(
                candidates.error,
                "Не удалось загрузить кандидатов.",
              )}{" "}
              <button
                type="button"
                className="button button--text button--compact"
                onClick={() => void candidates.refetch()}
              >
                Повторить
              </button>
            </Notice>
          ) : null}
          {candidates.isSuccess ? (
            <TargetPicker
              service={service}
              candidates={list}
              conflict={conflict}
              selected={selected}
              onSelect={(id, name) => {
                key.reset();
                setTarget({ id, name });
              }}
            />
          ) : null}

          <label className="cp-check">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => {
                key.reset();
                setRemember(event.target.checked);
              }}
            />
            <span>
              Запомнить отправителя
              <small className="cp-hint">
                Следующие письма с этого адреса будут определяться
                автоматически.
              </small>
            </span>
          </label>

          {resolve.isError ? (
            <Notice>
              {requestErrorText(
                resolve.error,
                "Не удалось сопоставить письмо. Повторите попытку.",
              )}
            </Notice>
          ) : null}
          <button
            type="button"
            className="button button--primary"
            disabled={selected === null || resolve.isPending}
            onClick={() => resolve.mutate()}
          >
            {resolve.isPending ? "Сопоставляем…" : "Сопоставить с контрагентом"}
          </button>
          <p className="muted" aria-live="polite">
            {target === null
              ? "Выберите контрагента, чтобы продолжить."
              : `Выбрано: ${target.name}`}
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Очередь писем, которые система не смогла отнести к контрагенту сама:
 * отправитель неизвестен или подошли сразу несколько контрагентов.
 */
export function SenderResolutionQueue({
  service,
  canManage,
  selectedId,
  onSelect,
}: {
  service: CounterpartyService;
  canManage: boolean;
  selectedId?: string;
  onSelect: (id: string | undefined) => void;
}) {
  const [state, setState] = useState<InboxState>("pending");
  const [notice, setNotice] = useState<string | null>(null);
  const messages = useQuery({
    queryKey: counterpartyKeys.inbox(state),
    queryFn: () =>
      service.unresolvedMessages({
        resolution_state: state,
        per_page: INBOX_LIMIT,
      }),
  });
  const items = messages.data ?? [];
  const selected = items.find((item) => item.id === selectedId);

  return (
    <section className="cp-inbox" aria-labelledby="cp-inbox-title">
      <div className="cp-inbox__head">
        <h2 id="cp-inbox-title">Письма без контрагента</h2>
        <div className="field cp-field cp-inbox__filter">
          <label htmlFor="cp-inbox-state">
            <span>Показать</span>
          </label>
          <Select
            id="cp-inbox-state"
            value={state}
            onChange={(event) => {
              setState(event.target.value as InboxState);
              onSelect(undefined);
            }}
          >
            {(Object.keys(stateLabels) as InboxState[]).map((value) => (
              <option key={value} value={value}>
                {stateLabels[value]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {notice === null ? null : (
        <p className="operation-notice operation-notice--success" role="status">
          {notice}
        </p>
      )}
      {messages.isLoading ? <p aria-busy="true">Загружаем письма…</p> : null}
      {messages.isError ? (
        <Notice>
          {requestErrorText(messages.error, "Не удалось загрузить письма.")}{" "}
          <button
            type="button"
            className="button button--text button--compact"
            onClick={() => void messages.refetch()}
          >
            Повторить
          </button>
        </Notice>
      ) : null}
      {messages.isSuccess && items.length === 0 ? (
        <p className="cp-empty">
          Все письма отнесены к контрагентам. Когда придёт письмо от
          неизвестного отправителя или возникнет конфликт, оно появится здесь.
        </p>
      ) : null}

      {items.length >= INBOX_LIMIT ? (
        <p className="muted">
          Показаны первые {INBOX_LIMIT} писем. Разберите их — появятся
          следующие.
        </p>
      ) : null}
      {items.length > 0 ? (
        <div className="cp-layout" data-detail={selected ? "open" : "closed"}>
          <ul className="cp-list cp-list--messages" aria-label="Письма">
            {items.map((message) => (
              <li key={message.id}>
                <button
                  type="button"
                  className="cp-list__item"
                  aria-current={message.id === selectedId ? "true" : undefined}
                  onClick={() => {
                    setNotice(null);
                    onSelect(message.id);
                  }}
                >
                  <span className="cp-list__title">
                    {message.subject ?? "Без темы"}
                  </span>
                  <span className="muted">{senderLabel(message)}</span>
                  <span className="cp-list__meta">
                    <StateBadge method={message.sender_resolution_method} />
                    <span className="muted">
                      {formatDate(message.received_at)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="cp-detail">
            {selected === undefined ? (
              <p className="cp-empty">
                Выберите письмо слева: здесь появятся варианты контрагентов.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  className="button button--text button--compact cp-back"
                  onClick={() => onSelect(undefined)}
                >
                  ← К списку
                </button>
                <MessageResolution
                  key={selected.id}
                  service={service}
                  message={selected}
                  canManage={canManage}
                  onResolved={(target, remembered) => {
                    setNotice(
                      `Письмо сопоставлено${target ? ` с «${target}»` : ""}.${remembered ? " Отправитель запомнен для будущих писем." : ""}`,
                    );
                    onSelect(undefined);
                  }}
                />
              </>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
